import { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Beta invite request page shown at "/". The product is live; access is
 * invite only. The full marketing site is still reachable at "/landing".
 *
 * Submissions go to the shared FormNinja form; the hidden "app" field is what
 * separates one product's requests from another's, so it must be set per site.
 */
const BETA_ENDPOINT = 'https://frm.ninja/f/2d2ba62a-2d6e-417b-8ef0-57fec6f59806';
const APP_NAME = 'Statsmatic';

export function BetaInvite() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    const formData = new FormData(e.currentTarget);

    try {
      const response = await fetch(BETA_ENDPOINT, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        setError('That did not go through. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-white">
      {/* Chart-grid backdrop: the product is analytics, so the page sits on graph paper. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.18]"
        style={{
          backgroundImage:
            'linear-gradient(to right, #0284c7 1px, transparent 1px), linear-gradient(to bottom, #0284c7 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 30%, transparent 100%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 80% 60% at 50% 30%, black 30%, transparent 100%)',
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 left-1/2 h-[36rem] w-[36rem] -translate-x-1/2 rounded-full bg-primary-400/20 blur-3xl"
      />

      <div className="relative flex min-h-screen flex-col">
        <header className="px-6 py-8 sm:px-10">
          <Link to="/" className="inline-flex items-center space-x-2">
            <img src="/logo.png" alt="" className="h-8 w-8" />
            <img src="/logo-text.png" alt={APP_NAME} className="h-6" />
          </Link>
        </header>

        <main className="flex flex-1 items-center px-6 pb-16 sm:px-10">
          <div className="mx-auto w-full max-w-2xl">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary-600">
              Invite only beta
            </p>

            <h1 className="mt-6 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
              Every site you run.
              <br />
              <span className="text-primary-600">One dashboard.</span>
            </h1>

            <p className="mt-6 max-w-xl text-lg leading-relaxed text-gray-600">
              Statsmatic is privacy-first analytics built for people shipping more than one
              product. Track every site you run from a single dashboard.
            </p>
            <p className="mt-4 max-w-xl text-lg leading-relaxed text-gray-600">
              We are running an invite only beta right now. If you are interested, leave
              your email and we will get in touch.
            </p>

            <div className="mt-10">
              {submitted ? (
                <div className="flex items-start gap-4 rounded-xl border border-primary-200 bg-primary-50 p-6">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-600">
                    <svg
                      className="h-5 w-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Request received</h2>
                    <p className="mt-1 text-gray-600">
                      We will be in touch about a beta invite. No spam, no noise.
                    </p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate={false}>
                  {/* Honeypot. FormNinja looks for "_fax" specifically. */}
                  <div
                    style={{ position: 'absolute', left: '-9999px' }}
                    aria-hidden="true"
                  >
                    <input type="text" name="_fax" tabIndex={-1} autoComplete="off" />
                  </div>

                  {/* Tells the shared beta form which product this came from. */}
                  <input type="hidden" name="app" value={APP_NAME} />

                  <label htmlFor="beta-email" className="sr-only">
                    Email address
                  </label>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <input
                      type="email"
                      id="beta-email"
                      name="email"
                      required
                      autoComplete="email"
                      placeholder="you@example.com"
                      className="w-full flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex items-center justify-center rounded-lg bg-primary-600 px-6 py-3 font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {submitting ? 'Sending...' : 'Request an invite'}
                    </button>
                  </div>

                  {error && (
                    <p role="alert" className="mt-3 text-sm text-red-600">
                      {error}
                    </p>
                  )}

                  <p className="mt-3 text-sm text-gray-500">
                    We only use your email to contact you about the beta.
                  </p>
                </form>
              )}
            </div>
          </div>
        </main>

        <footer className="px-6 pb-10 sm:px-10">
          <div className="mx-auto flex w-full max-w-2xl flex-col gap-2 text-sm text-gray-500 text-center sm:flex-row sm:items-center sm:justify-between sm:text-left">
            <p>
              <span className="block sm:inline">
                &copy; {new Date().getFullYear()}{' '}
                <a
                  href="https://horizon-group.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-gray-700"
                >
                  Horizon Group
                </a>
                .
              </span>{' '}
              <span className="block sm:inline">
                Built with ❤️ by{' '}
                <a
                  href="https://jason.camp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-gray-700"
                >
                  Jason Camp
                </a>
              </span>
            </p>
            <p className="flex items-center justify-center gap-3 sm:justify-start">
              <Link to="/privacy" className="hover:text-gray-700">
                Privacy
              </Link>
              <span aria-hidden="true">·</span>
              <Link to="/terms" className="hover:text-gray-700">
                Terms
              </Link>
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default BetaInvite;
